import { ref, toValue } from 'vue';
import { makeDestructurable } from '@vueuse/shared';
import { createBubble } from '/app/composables/bubble';
import { useExperiencePoints } from '/app/composables/brain';
import { bus } from '/app/plugins/bus';
import { BELL_FLY_TARGET, POINTS_EARNED } from '/app/constant/game-dash';

// Long enough to read "+5 points", short enough that the flight can follow
// without the whole sequence outlasting the action that earned it.
const DWELL_MS = 700;

/**
 * A bubble that announces earned points and throws itself at the navbar bell.
 *
 * Use it anywhere an action scores. `earn()` resolves once the dot has landed,
 * so a caller that navigates afterwards keeps the animation coherent.
 *
 * @param {object} options - Rest is forwarded to createBubble
 * @param {string} [options.fqdn] - Neuron the amount is read from
 * @param {string} [options.unit] - Currency the label names, default `points`
 * @returns {Object} - `{ target, earn }`, also destructurable as an array
 * @property {Ref<HTMLElement>} target - Element the bubble points at
 * @property {Function} earn - `earn(amount, { unit, ...options })`, returns a
 *           Promise. Both arguments are optional: the amount defaults to the
 *           neuron's, the unit to the one this bubble was created with.
 */
export const createPointsBubble = ({ fqdn, unit: defaultUnit = 'points', ...defaultOptions } = {}) => {
    const { target, flash } = createBubble({
        duration: DWELL_MS,
        flyTo: BELL_FLY_TARGET,
        ...defaultOptions,
    });

    // One request per fqdn for the whole page - the composable caches and
    // single-flights - so a board full of vote buttons still asks once.
    const { points } = fqdn ? useExperiencePoints(fqdn) : { points: ref() };

    const earn = (amount = points, { unit = defaultUnit, ...options } = {}) => {
        const amountValue = toValue(amount)
        // The brain has no award for this action, or has not answered yet.
        // Staying quiet beats announcing a number the score will not match.
        if (!amountValue) {
            return Promise.resolve();
        }

        return flash(`+${amountValue} ${unit}`, {
            ...options,
            onHidden: () => {
                options.onHidden?.();

                // Fires on landing, not on click: the bell reacts when the dot
                // reaches it.
                bus(POINTS_EARNED, { amount: amountValue });
            },
        });
    };

    return makeDestructurable(
        { target, earn },
        [target, earn],
    );
};
